/** Native transport only; OAuth provider verification remains on the API. */
export const NATIVE_SOCIAL_CALLBACK = 'com.masterdaweb.app://auth/social/callback'
export const NATIVE_SOCIAL_KEY = 'mw_native_social_flow_v1'
export const NATIVE_SOCIAL_TTL = 10 * 60 * 1000
export type NativeSocialProvider = 'google' | 'microsoft' | 'facebook'
export type SocialParams = Record<string, string>

export interface NativeSocialFlow {
  state: string
  verifier: string
  provider: NativeSocialProvider
  expiresAt: number
  redirect: string
}

export function safeSocialRedirect(value?: string): string {
  return value?.startsWith('/') && !value.startsWith('//') && !/[\\\x00-\x1f]/.test(value) ? value : '/'
}

function base64url(bytes: Uint8Array): string {
  return btoa(String.fromCharCode(...bytes)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

export async function nativeChallenge(verifier: string): Promise<string> {
  return base64url(new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier))))
}

export function parseNativeCallback(value: string): { code: string, state: string } | null {
  try {
    const url = new URL(value)
    if (`${url.protocol}//${url.host}${url.pathname}` !== NATIVE_SOCIAL_CALLBACK || url.username || url.password || url.hash) return null
    if (url.searchParams.getAll('code').length !== 1 || url.searchParams.getAll('state').length !== 1) return null
    const code = url.searchParams.get('code')!
    const state = url.searchParams.get('state')!
    return /^[A-Za-z0-9_-]{43}$/.test(code) && /^[A-Za-z0-9_-]{43}$/.test(state) ? { code, state } : null
  }
  catch { return null }
}

interface NativeSocialDependencies {
  read: () => Promise<NativeSocialFlow | null>
  write: (flow: NativeSocialFlow) => Promise<void>
  remove: () => Promise<void>
  open: (url: string) => Promise<void>
  close: () => Promise<void>
  exchange: (code: string, verifier: string) => Promise<SocialParams>
  result: (params: SocialParams) => Promise<void>
  pending: (provider: NativeSocialProvider | null) => void
  now?: () => number
}

/** Adapter-driven so cold starts, callback races and wrong-state links are testable. */
export function createNativeSocialLogin(deps: NativeSocialDependencies) {
  const now = deps.now ?? Date.now
  let handling = false
  let starting = false

  async function active() {
    const flow = await deps.read()
    if (flow && flow.expiresAt > now()) return flow
    if (flow) await deps.remove()
    return null
  }

  async function start(provider: NativeSocialProvider, startUrl: string, redirect?: string) {
    if (starting || handling) return
    starting = true
    deps.pending(provider)
    try {
      const url = new URL(startUrl)
      if (url.protocol !== 'https:') throw new Error('Native social login requires HTTPS')
      const verifier = base64url(crypto.getRandomValues(new Uint8Array(32)))
      const flow: NativeSocialFlow = {
        verifier, provider, state: base64url(crypto.getRandomValues(new Uint8Array(32))),
        expiresAt: now() + NATIVE_SOCIAL_TTL, redirect: safeSocialRedirect(redirect),
      }
      url.searchParams.set('display', 'native')
      url.searchParams.set('nativeState', flow.state)
      url.searchParams.set('codeChallenge', await nativeChallenge(verifier))
      url.searchParams.set('codeChallengeMethod', 'S256')
      url.searchParams.set('redirect', flow.redirect)
      // Persist BEFORE leaving: the OS may reclaim the WebView while signing in.
      await deps.write(flow)
      await deps.open(url.href)
    }
    catch (error) {
      await deps.remove()
      deps.pending(null)
      throw error
    }
    finally { starting = false }
  }

  async function handle(url: string): Promise<boolean> {
    const callback = parseNativeCallback(url)
    if (!callback || handling) return false
    handling = true
    try {
      const flow = await active()
      if (!flow || flow.state !== callback.state) return false
      deps.pending(flow.provider)
      await deps.close().catch(() => {}) // Browser.close is iOS-only on older shells.
      let params: SocialParams
      try {
        params = await deps.exchange(callback.code, flow.verifier)
      }
      catch {
        params = { error: 'generic' }
      }
      await deps.remove()
      deps.pending(null)
      // Return destination comes from this installation, never from a deep link.
      await deps.result({ ...params, redirect: flow.redirect })
      return true
    }
    finally { handling = false }
  }

  // Closing the browser is not proof of cancellation: a deep link also closes it.
  // Release the button but retain the proof for a racing callback or a cold start.
  function dismiss() { if (!handling) deps.pending(null) }
  return { start, handle, active, dismiss, isBusy: () => handling || starting }
}
